/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import {
  ArrowsClockwise,
  ArrowRight,
  Buildings,
  CalendarCheck,
  ChartLineUp,
  Download,
  Target,
  SignIn,
  SquaresFour,
  Users,
} from 'phosphor-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  FilterSelect,
  SearchSelect,
  FramedCard,
  FramedCardContent,
  Period,
  PeriodSelect,
  defaultPeriod,
  periodDays,
} from '../../components/ui';
import {
  BarList,
  Delta,
  DonutChart,
  RadarChart,
  Sparkline,
  TrendChart,
  formatFull,
} from '../../components/charts';
import { PageTitle } from '../../components/PageTitle';
import { DashboardActivity, actionLabels, deltaPercent, emptyDashboard } from './dashboardData';
import { api } from '../../services/api';
import { DataSkeleton, ErrorState } from '../../components/states';

const COMPARE_OPTIONS = [
  { value: 'previous', label: 'Período anterior' },
  { value: 'none', label: 'Sem comparativo' },
];

const formatDayLabel = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d)
    .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
    .replace('.', '');
};

const breakdownLinks: Record<string, string> = {
  login: '/users',
  module_view: '/contents/modules',
  week_view: '/contents/modules',
  material_view: '/extraMaterials',
  material_download: '/extraMaterials',
};

interface RatingItem {
  name: string;
  average: number;
}

interface RatingsState {
  loading: boolean;
  error: string | null;
  overall: RatingItem[];
  institutions: { id: string; name: string; ratings: RatingItem[] }[];
}

const toRatingList = (raw: any): RatingItem[] =>
  (raw ?? [])
    .map((item: any) => ({ name: item.name, average: parseFloat(item.average) }))
    .filter((item: RatingItem) => item.name && Number.isFinite(item.average));

const CardTitle = ({
  icon,
  children,
  right,
}: {
  icon?: React.ElementType;
  children: React.ReactNode;
  right?: React.ReactNode;
}) => {
  const IconComponent = icon;
  return (
    <Flex align="center" justify="space-between" gap="12px" h="30px">
      <Flex align="center" gap="8px" minW={0}>
        {IconComponent && (
          <Box color="content.subtle" display="flex">
            <IconComponent size={14} />
          </Box>
        )}
        <Text fontSize="13px" lineHeight="20px" fontWeight={500} color="content.strong" isTruncated>
          {children}
        </Text>
      </Flex>
      {right}
    </Flex>
  );
};

const EmptyNote = ({ children }: { children: React.ReactNode }) => (
  <Flex flex="1" align="center" justify="center" py="24px">
    <Text fontSize="13px" color="content.faint" textAlign="center">
      {children}
    </Text>
  </Flex>
);

const MetricCard = ({
  icon,
  label,
  value,
  previous,
  series,
  hint,
  compare,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  previous: number;
  series: number[];
  hint: string;
  compare: boolean;
}) => {
  const delta = deltaPercent(value, previous);
  let tone: 'positive' | 'negative' | 'neutral' = 'neutral';
  if (delta != null && delta > 0) tone = 'positive';
  if (delta != null && delta < 0) tone = 'negative';
  return (
    <FramedCard>
      <FramedCardContent p="16px" gap="12px">
        <CardTitle icon={icon}>{label}</CardTitle>
        <Flex align="flex-end" justify="space-between" gap="12px">
          <Box minW={0}>
            <Flex align="baseline" gap="8px">
              <Text
                fontFamily="display"
                fontSize="24px"
                lineHeight="30px"
                fontWeight={500}
                color="content.strong"
              >
                {formatFull(value)}
              </Text>
              {compare && <Delta value={delta} />}
            </Flex>
            <Text fontSize="12px" lineHeight="16px" color="content.subtle" noOfLines={1}>
              {hint}
            </Text>
          </Box>
          <Sparkline data={series} tone={compare ? tone : 'neutral'} />
        </Flex>
      </FramedCardContent>
    </FramedCard>
  );
};

export const Home = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const [compareMode, setCompareMode] = useState<'previous' | 'none'>('previous');
  const [refreshKey, setRefreshKey] = useState(0);
  const compare = compareMode === 'previous';

  const [activity, setActivity] = useState<DashboardActivity>(() =>
    emptyDashboard(periodDays(period), period.from, period.to)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const toIso = (d: Date) => d.toISOString().slice(0, 10);
    setLoading(true);
    setError(null);
    api
      .get('/v1/admin/dashboard/activity', {
        params: { start: toIso(period.from), end: toIso(period.to) },
      })
      .then((response) => {
        if (cancelled) return;
        const payload = response.data?.data;
        if (payload && payload.period) setActivity(payload as DashboardActivity);
        else setError('Resposta inválida do servidor.');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.msg || 'Não foi possível carregar a atividade.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period, refreshKey]);

  const [ratings, setRatings] = useState<RatingsState>({
    loading: true,
    error: null,
    overall: [],
    institutions: [],
  });
  const [ratingsScope, setRatingsScope] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setRatings((prev) => ({ ...prev, loading: true, error: null }));
    Promise.all([api.get('/v1/admin/dashboard'), api.get('/v1/admin/dashboard/institutions')])
      .then(([overallRes, institutionsRes]) => {
        if (cancelled) return;
        setRatings({
          loading: false,
          error: null,
          overall: toRatingList(overallRes.data?.data),
          institutions: (institutionsRes.data?.data ?? []).map((item: any) => ({
            id: String(item.id),
            name: item.name,
            ratings: toRatingList(item.ratings),
          })),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setRatings({
          loading: false,
          error: err?.response?.data?.msg || 'Não foi possível carregar as avaliações.',
          overall: [],
          institutions: [],
        });
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const ratingScopeOptions = useMemo(
    () => [
      { value: 'all', label: 'Todas as instituições' },
      ...ratings.institutions.map((i) => ({ value: i.id, label: i.name })),
    ],
    [ratings.institutions]
  );

  const radarPoints = useMemo(() => {
    const axes: string[] = [];
    const push = (list: RatingItem[]) =>
      list.forEach((r) => {
        if (!axes.includes(r.name)) axes.push(r.name);
      });
    push(ratings.overall);
    ratings.institutions.forEach((i) => push(i.ratings));

    const selected =
      ratingsScope === 'all'
        ? ratings.overall
        : ratings.institutions.find((i) => i.id === ratingsScope)?.ratings ?? [];
    const byName = new Map(selected.map((r) => [r.name, r.average]));
    return axes.map((name) => ({ name, value: byName.get(name) ?? null }));
  }, [ratings, ratingsScope]);

  const radarAverage = useMemo(() => {
    const values = radarPoints.map((p) => p.value).filter((v): v is number => v != null);
    if (values.length === 0) return null;
    return values.reduce((acc, v) => acc + v, 0) / values.length;
  }, [radarPoints]);

  const renderRatings = () => {
    if (ratings.loading) {
      return <DataSkeleton variant="cards" rows={1} label="Carregando avaliações" />;
    }
    if (ratings.error) return <EmptyNote>{ratings.error}</EmptyNote>;
    if (radarPoints.length === 0) {
      return <EmptyNote>Nenhuma avaliação respondida até o momento.</EmptyNote>;
    }
    return (
      <Flex justify="center" align="center" gap="40px" wrap="wrap" py="4px">
        <Box w={{ base: '100%', md: '440px' }} maxW="100%">
          <RadarChart points={radarPoints} height={300} />
        </Box>
        <Flex direction="column" gap="2px" w={{ base: '100%', md: '300px' }}>
          <Flex align="baseline" justify="space-between" mb="6px">
            <Text fontSize="12px" color="content.faint">
              Nota média
            </Text>
            <Text fontFamily="display" fontSize="20px" fontWeight={500} color="content.strong">
              {radarAverage != null ? radarAverage.toFixed(1) : '-'}
            </Text>
          </Flex>
          {radarPoints.map((point) => (
            <Flex
              key={point.name}
              align="center"
              justify="space-between"
              gap="12px"
              h="30px"
              borderTop="1px solid"
              borderColor="line.hairline"
            >
              <Text fontSize="13px" color="content.body">
                {point.name}
              </Text>
              <Text
                fontFamily="mono"
                fontSize="12px"
                color={point.value == null ? 'content.faint' : 'content.strong'}
              >
                {point.value == null ? '-' : point.value.toFixed(1)}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    );
  };

  const timelineData = useMemo(
    () =>
      activity.timeline.current.map((point, index) => ({
        label: formatDayLabel(point.date),
        value: point.value,
        compare: compare ? activity.timeline.previous[index]?.value : undefined,
      })),
    [activity, compare]
  );

  const hasData = activity.timeline.current.length > 0;
  const timelineTotal = timelineData.reduce((acc, p) => acc + p.value, 0);
  const timelinePrevious = timelineData.reduce((acc, p) => acc + (p.compare ?? 0), 0);

  return (
    <Box>
      <PageTitle
        description="Atividade da plataforma, uso dos conteúdos e evolução das avaliações."
        action={
          <Button
            size="sm"
            variant="outline"
            leftIcon={<ArrowsClockwise size={14} />}
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            Recarregar
          </Button>
        }
      >
        Dashboard
      </PageTitle>

      <Flex align="center" gap="8px" flexWrap="wrap" mb="16px">
        <PeriodSelect value={period} onChange={setPeriod} />
        <FilterSelect
          ariaLabel="Comparativo"
          icon={CalendarCheck}
          options={COMPARE_OPTIONS}
          value={compareMode}
          onChange={(value) => setCompareMode(value as 'previous' | 'none')}
        />
        <Text ml="auto" fontFamily="mono" fontSize="12px" color="content.faint">
          {period.from.toLocaleDateString('pt-BR')} – {period.to.toLocaleDateString('pt-BR')}
        </Text>
      </Flex>

      {error && (
        <Box mb="12px">
          <ErrorState description={error} onRetry={() => setRefreshKey((k) => k + 1)} />
        </Box>
      )}

      {loading && !error && !hasData && (
        <DataSkeleton variant="cards" rows={4} label="Carregando atividade" />
      )}

      <Box opacity={loading && hasData ? 0.6 : 1} transition="opacity 150ms ease">
        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
          gap="12px"
          mb="12px"
        >
          <MetricCard
            icon={Users}
            label="Usuários ativos"
            value={activity.activeUsers.total}
            previous={activity.activeUsers.previous}
            series={activity.activeUsers.series}
            hint="Com atividade no período"
            compare={compare}
          />
          <MetricCard
            icon={SignIn}
            label="Logins"
            value={activity.logins.total}
            previous={activity.logins.previous}
            series={activity.logins.series}
            hint="Entradas no app"
            compare={compare}
          />
          <MetricCard
            icon={ChartLineUp}
            label="Acessos a conteúdo"
            value={activity.contentViews.total}
            previous={activity.contentViews.previous}
            series={activity.contentViews.series}
            hint="Módulos, semanas, materiais"
            compare={compare}
          />
          <MetricCard
            icon={Download}
            label="Downloads"
            value={activity.downloads.total}
            previous={activity.downloads.previous}
            series={activity.downloads.series}
            hint="Materiais extras"
            compare={compare}
          />
        </Grid>

        <Grid templateColumns={{ base: '1fr', xl: 'repeat(3, 1fr)' }} gap="12px" mb="12px">
          <GridItem minW={0} colSpan={{ base: 1, xl: 2 }}>
            <FramedCard h="100%">
              <FramedCardContent p="16px" gap="12px" h="100%">
                <CardTitle icon={ChartLineUp}>Atividade ao longo do tempo</CardTitle>
                <Flex align="baseline" gap="8px">
                  <Text
                    fontFamily="display"
                    fontSize="24px"
                    lineHeight="30px"
                    fontWeight={500}
                    color="content.strong"
                  >
                    {formatFull(timelineTotal)}
                  </Text>
                  {compare && <Delta value={deltaPercent(timelineTotal, timelinePrevious)} />}
                </Flex>
                <TrendChart data={timelineData} showCompare={compare} area height={240} />
                <Flex justify="center" gap="20px">
                  <Flex align="center" gap="6px">
                    <Box w="8px" h="8px" borderRadius="full" bg="state.link" />
                    <Text fontSize="12px" color="content.body">
                      Período atual
                    </Text>
                  </Flex>
                  {compare && (
                    <Flex align="center" gap="6px">
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        border="1.5px dotted"
                        borderColor="state.link"
                      />
                      <Text fontSize="12px" color="content.body">
                        Período anterior
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </FramedCardContent>
            </FramedCard>
          </GridItem>
          <GridItem minW={0}>
            <FramedCard h="100%">
              <FramedCardContent p="16px" gap="4px" h="100%">
                <CardTitle icon={SquaresFour}>Detalhamento de acessos</CardTitle>
                <Flex direction="column" mt="8px" gap="4px">
                  {activity.breakdown.map((item, index) => {
                    const delta = deltaPercent(item.total, item.previous);
                    let tone: 'positive' | 'negative' | 'neutral' = 'neutral';
                    if (compare && delta != null && delta > 0) tone = 'positive';
                    if (compare && delta != null && delta < 0) tone = 'negative';
                    return (
                      <Flex
                        key={item.action}
                        align="center"
                        justify="space-between"
                        gap="12px"
                        h="32px"
                        px="12px"
                        borderRadius="sm"
                        bg={index % 2 === 1 ? 'surface.selected' : 'transparent'}
                      >
                        <Text
                          as={RouterLink}
                          to={breakdownLinks[item.action]}
                          fontSize="13px"
                          color="state.link"
                          minW={0}
                          isTruncated
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {actionLabels[item.action]}
                        </Text>
                        <Flex align="center" gap="12px" flexShrink={0}>
                          <Text
                            fontFamily="mono"
                            fontSize="13px"
                            color="content.strong"
                            minW="40px"
                            textAlign="right"
                          >
                            {formatFull(item.total)}
                          </Text>
                          <Sparkline data={item.series} tone={tone} width={56} height={20} />
                        </Flex>
                      </Flex>
                    );
                  })}
                </Flex>
              </FramedCardContent>
            </FramedCard>
          </GridItem>
        </Grid>

        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }}
          gap="12px"
          mb="12px"
        >
          <FramedCard>
            <FramedCardContent p="16px" gap="12px" h="100%">
              <CardTitle
                icon={SquaresFour}
                right={
                  <Button
                    size="xs"
                    variant="ghost"
                    rightIcon={<ArrowRight size={12} />}
                    onClick={() => navigate('/contents/modules')}
                  >
                    Ver todos
                  </Button>
                }
              >
                Módulos mais acessados
              </CardTitle>
              {activity.topModules.length === 0 && (
                <EmptyNote>Nenhum módulo acessado no período.</EmptyNote>
              )}
              <BarList
                items={activity.topModules.map((m) => ({
                  id: m.id,
                  icon: <SquaresFour size={14} />,
                  label: (
                    <Text
                      as={RouterLink}
                      to={`/contents/modules/${m.id}/weeks`}
                      color="state.link"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {m.name}
                    </Text>
                  ),
                  value: m.value,
                  hint: 'acessos',
                }))}
              />
            </FramedCardContent>
          </FramedCard>

          <FramedCard>
            <FramedCardContent p="16px" gap="12px" h="100%">
              <CardTitle
                icon={Buildings}
                right={
                  <Button
                    size="xs"
                    variant="ghost"
                    rightIcon={<ArrowRight size={12} />}
                    onClick={() => navigate('/institutions/list')}
                  >
                    Ver todas
                  </Button>
                }
              >
                Instituições mais ativas
              </CardTitle>
              {activity.topInstitutions.length === 0 && (
                <EmptyNote>Nenhuma instituição com atividade no período.</EmptyNote>
              )}
              <BarList
                items={activity.topInstitutions.map((i) => ({
                  id: i.id,
                  icon: <Buildings size={14} />,
                  label: (
                    <Text
                      as={RouterLink}
                      to={`/institutions/list/${i.id}/teams`}
                      color="state.link"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {i.name}
                    </Text>
                  ),
                  value: i.value,
                  hint: 'acessos',
                }))}
              />
            </FramedCardContent>
          </FramedCard>

          <FramedCard>
            <FramedCardContent p="16px" gap="12px" h="100%">
              <CardTitle icon={Users}>Acessos por perfil</CardTitle>
              {activity.accessByProfile.length === 0 ? (
                <EmptyNote>
                  Nenhum acesso de tutores, professores ou responsáveis no período.
                </EmptyNote>
              ) : (
                <Flex flex="1" align="center" justify="center" py="8px">
                  <DonutChart
                    slices={activity.accessByProfile.map((p) => ({
                      id: String(p.id),
                      label: p.name,
                      value: p.value,
                    }))}
                  />
                </Flex>
              )}
            </FramedCardContent>
          </FramedCard>
        </Grid>

        <FramedCard>
          <FramedCardContent p="16px" gap="12px">
            <CardTitle
              icon={Target}
              right={
                ratings.institutions.length > 0 ? (
                  <SearchSelect
                    ariaLabel="Instituição"
                    searchLabel="instituição"
                    minSearchCount={0}
                    icon={Buildings}
                    value={ratingsScope}
                    onChange={setRatingsScope}
                    options={ratingScopeOptions}
                  />
                ) : undefined
              }
            >
              Médias das avaliações
            </CardTitle>
            {renderRatings()}
          </FramedCardContent>
        </FramedCard>
      </Box>
    </Box>
  );
};
